/**
 * Right-click drill-down for a report: one row's breakdown spliced in beneath it, as Prophecy's
 * `Drill down by ›` does, with each drilled child expandable again when it has children.
 *
 * Rows are addressed by key, not member id: a drilled child's key is `<parent key>>` + its member,
 * because the same country appears under Echo and under FireTV. Children live here rather than in
 * the query response, which a re-query replaces wholesale. A report whose own rows form a tree opens
 * with that tree expanded.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { drillReport, listDrillDimensions } from 'src/components/star-grid/api/reportEngineClient';
import type {
  QueryResponse,
  DrillDimension,
  DrillRequest,
  ReportQueryRequest,
  ReportQueryRow,
  StarRocksQuery
} from 'src/components/star-grid/api/reportEngineTypes';
import { errorMessage } from 'src/components/star-grid/utils/errorMessage';

/** Separates a drilled child's member from its parent's key. Cannot occur in a TM1 member name. */
export const DRILL_KEY_SEPARATOR = '>';

/** The key a row is addressed by: its drill key when it is a drilled child, else its member id. */
export const reportRowKey = (row: Pick<ReportQueryRow, 'member_id'> & { readonly drill_key?: string | null }): string =>
  row.drill_key ?? row.member_id;

export interface UseReportDrillResult {
  /**
   * Dimensions the menu offers: undefined until the menu first asks (`loadDimensions`) and the load
   * returns, so opening a report spends no request on a menu nobody opens. Empty when nothing drills.
   */
  readonly dimensions: readonly DrillDimension[] | undefined;
  readonly loadDimensions: () => void;
  /**
   * Runs a drill and remembers its children under `key`. For a top-level row `target` names the
   * dimension; for a drilled child it may be omitted, and the child is expanded along its own.
   */
  readonly drill: (key: string, indent: number, target?: DrillDimension) => Promise<void>;
  /** Whether a row already has children showing. */
  readonly isExpanded: (key: string) => boolean;
  /** Drops one row's children, and theirs. */
  readonly collapse: (key: string) => void;
  /** On a two-dimension report: opens or closes an OUTER line (by its member) on its own hierarchy. */
  readonly toggleOuter: (outerMember: string, indent: number) => Promise<void>;
  readonly isOuterExpanded: (outerMember: string) => boolean;
  /** Drops every expansion — used when the report re-queries into a different shape. */
  readonly reset: () => void;
  /** The response with children spliced beneath their parents, ready for the grid. */
  readonly withChildren: (response: QueryResponse | undefined) => QueryResponse | undefined;
  readonly busy: boolean;
  readonly error: string | undefined;
  /** Dismisses a reported failure, so the alert does not outlive the attempt. */
  readonly clearError: () => void;
  /** The server's share of the most recent expansion, for the load-time badge. */
  readonly serverMs: number | undefined;
  /** The StarRocks queries the last drill ran, for the Show stats panel. */
  readonly queries: readonly StarRocksQuery[] | undefined;
}

interface DrillAxis {
  readonly dimension: string;
  readonly hierarchy?: string;
}

export interface UseReportDrillOptions {
  /**
   * The report's own row axis, which a row with no drill dimension of its own expands along. On a
   * two-dimension report a row expands along the INNER axis (`nested`): its second column.
   */
  readonly rowAxis?: DrillAxis & { readonly nested?: DrillAxis };
  /** The report's own rows, whose `tree_parent` links say which rows are already listed children. */
  readonly rows?: readonly ReportQueryRow[];
  /**
   * The viewer's query knobs (filter-bar overrides, view controls, show-by, zero suppression), so a
   * drill breaks down what the grid is showing rather than the saved report.
   */
  readonly query?: Partial<ReportQueryRequest>;
  /**
   * The deck the drill MENU depends on. Only a report with per-deck overrides has a per-deck menu;
   * the rest pass '' so a deck change reuses the menu instead of asking again.
   */
  readonly dimensionsDeck?: string;
}

/**
 * A drilled child of a two-dimension row, laid out in that row's columns: the outer label stays in
 * the first column and the child takes the second, one level under its parent there. It keeps the
 * pair's inner member and outer scope, so expanding it again drills within the same pair.
 */
const asPairChild = (row: ReportQueryRow, parent: ReportQueryRow): Partial<ReportQueryRow> => {
  if (parent.secondary_label === undefined || !parent.drill_member) return {};
  return {
    label: parent.label,
    secondary_label: row.label,
    drill_member: parent.drill_member,
    drill_context: parent.drill_context,
    ...(typeof parent.secondary_indent === 'number' ? { indent: parent.indent ?? 0, secondary_indent: parent.secondary_indent + 1 } : {})
  };
};

type DrillTarget = Omit<DrillRequest, keyof ReportQueryRequest>;

/** Whether two member sets hold the same ids, so the collapse state can bail out instead of re-rendering. */
const sameMembers = (left: ReadonlySet<string>, right: ReadonlySet<string>): boolean =>
  left.size === right.size && [...left].every((member) => right.has(member));

/** Drops an outer line's children and, recursively, those of any outer child opened beneath it. */
const withoutOuter = (
  current: Readonly<Record<string, readonly ReportQueryRow[]>>,
  member: string
): Readonly<Record<string, readonly ReportQueryRow[]>> => {
  const { [member]: dropped, ...rest } = current;
  return (dropped ?? []).reduce((next, row) => (row.outer_member ? withoutOuter(next, row.outer_member) : next), rest);
};

export const useReportDrill = (
  reportId: string | undefined,
  deckType: string | undefined,
  options: UseReportDrillOptions = {}
): UseReportDrillResult => {
  const { rowAxis, rows: listedRows, query } = options;
  const menuDeck = options.dimensionsDeck ?? deckType;
  const [loadedMenu, setLoadedMenu] = useState<{ readonly scope: string; readonly list: readonly DrillDimension[] } | undefined>(undefined);
  // Wanted per (report, deck): a deck change waits for the next ask instead of loading alongside the
  // new deck's query.
  const scopeKey = `${reportId ?? ''}|${menuDeck ?? ''}`;
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const [wantedFor, setWantedFor] = useState<string | undefined>(undefined);
  const menuWanted = wantedFor === scopeKey;
  // Another report's (or per-deck report's) menu is never shown for this one.
  const dimensions = loadedMenu?.scope === scopeKey ? loadedMenu.list : undefined;
  const loadDimensions = useCallback(() => setWantedFor(scopeRef.current), []);
  const [children, setChildren] = useState<Readonly<Record<string, readonly ReportQueryRow[]>>>({});
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  // A two-dimension report's OUTER lines expand apart from their inner members: children keyed by
  // outer member, and listed outer children hidden while their listed outer parent is collapsed.
  const [outerChildren, setOuterChildren] = useState<Readonly<Record<string, readonly ReportQueryRow[]>>>({});
  const [outerCollapsed, setOuterCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [serverMs, setServerMs] = useState<number | undefined>(undefined);
  const [queries, setQueries] = useState<readonly StarRocksQuery[] | undefined>(undefined);
  const listedParent = useMemo(() => {
    const parents = new Map<string, string>();
    for (const row of listedRows ?? []) if (row.tree_parent) parents.set(row.member_id, row.tree_parent);
    return parents;
  }, [listedRows]);
  const hasListedChildren = useCallback((key: string): boolean => [...listedParent.values()].includes(key), [listedParent]);
  const listedOuterParent = useMemo(() => {
    const parents = new Map<string, string>();
    for (const row of listedRows ?? []) if (row.outer_member && row.outer_tree_parent) parents.set(row.outer_member, row.outer_tree_parent);
    return parents;
  }, [listedRows]);
  const hasListedOuterChildren = useCallback((member: string): boolean => [...listedOuterParent.values()].includes(member), [listedOuterParent]);
  // Each response opens as it states: a listed node marked `expanded: false` (inner or outer) starts closed.
  // Bail out when the set is unchanged, so a caller that passes a fresh `rows` identity costs one extra
  // effect run rather than an unbounded render loop.
  useEffect(() => {
    const innerParents = new Set(listedParent.values());
    const outerParents = new Set(listedOuterParent.values());
    const rows = listedRows ?? [];
    const nextInner = new Set(rows.filter((row) => row.expanded === false && innerParents.has(row.member_id)).map((row) => row.member_id));
    const nextOuter = new Set(
      rows.filter((row) => row.outer_member && row.outer_expanded === false && outerParents.has(row.outer_member)).map((row) => row.outer_member!)
    );
    setCollapsed((current) => (sameMembers(current, nextInner) ? current : nextInner));
    setOuterCollapsed((current) => (sameMembers(current, nextOuter) ? current : nextOuter));
  }, [listedRows, listedParent, listedOuterParent]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const abortRef = useRef<AbortController | undefined>(undefined);

  useEffect(() => {
    if (!reportId || !menuWanted) return undefined;
    const scope = scopeKey;
    const controller = new AbortController();
    listDrillDimensions(reportId, menuDeck || undefined, controller.signal)
      .then((found) => {
        if (!controller.signal.aborted) setLoadedMenu({ scope, list: found });
      })
      // A missing menu must not break the report: the grid just offers no drill.
      .catch(() => {
        if (!controller.signal.aborted) setLoadedMenu({ scope, list: [] });
      });
    return () => controller.abort();
  }, [reportId, menuDeck, menuWanted, scopeKey]);

  // One expansion at a time: a newer one aborts the last.
  const runDrill = useCallback(
    async (target: DrillTarget, keep: (response: QueryResponse) => void): Promise<void> => {
      if (!reportId) return;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setBusy(true);
      setError(undefined);
      try {
        const response = await drillReport(reportId, { ...query, ...target, deck_type: deckType ?? '', filters: {}, periods: [] }, controller.signal);
        if (controller.signal.aborted) return;
        keep(response);
        setServerMs(response.timings_ms?.total_ms);
        setQueries(response.queries);
      } catch (caught: unknown) {
        if (!controller.signal.aborted) setError(errorMessage(caught));
      } finally {
        if (!controller.signal.aborted) setBusy(false);
      }
    },
    [reportId, query, deckType]
  );

  const drill = useCallback(
    async (key: string, indent: number, target?: DrillDimension): Promise<void> => {
      if (!reportId) return;
      if (!target && hasListedChildren(key)) {
        setCollapsed((current) => new Set([...current].filter((existing) => existing !== key)));
        return;
      }
      // A drilled child is found among the stored children; a top-level row is not there.
      const child = Object.values(children)
        .flat()
        .find((row) => reportRowKey(row) === key);
      // A two-dimension row (`outer>inner`) drills its inner member with the outer one pinned; an
      // opened outer line's rows are pairs too.
      const listed = child ? undefined : [...(listedRows ?? []), ...Object.values(outerChildren).flat()].find((row) => reportRowKey(row) === key);
      const pair = child ?? listed;
      // A stacked row names its own axis; a side-by-side pair drills its inner member.
      const ownAxis = listed?.drill_axis
        ? listed.drill_axis === 'inner'
          ? rowAxis?.nested
          : rowAxis
        : rowAxis?.nested && listed?.drill_member
        ? rowAxis.nested
        : rowAxis;
      const dimension =
        target ??
        (child?.drill_dimension
          ? { dimension: child.drill_dimension, hierarchy: child.drill_hierarchy ?? child.drill_dimension, label: child.drill_dimension }
          : child
          ? undefined
          : ownAxis && { dimension: ownAxis.dimension, hierarchy: ownAxis.hierarchy ?? ownAxis.dimension, label: ownAxis.dimension });
      if (!dimension) return;
      // A top-level row expanded along its OWN hierarchy is rooted at itself: its children, not the
      // report's whole scope.
      const alongOwnAxis = !target && !child;
      const self = listed?.drill_member ?? key;
      // A stacked row's member_id is a prefixed `<dim>|<member>`, so its own drill_member roots it.
      const childRoot = child?.drill_axis && child.drill_member ? child.drill_member : child?.member_id;
      await runDrill(
        {
          // A child is expanded as its PARENT line pinned, rooted at the child's own member.
          member: pair?.drill_member ?? child?.parent_member_id ?? key,
          ...(child ? { root: childRoot ?? key } : alongOwnAxis ? { root: self } : {}),
          ...(pair?.drill_context?.length ? { context: pair.drill_context, row_id: child?.parent_member_id ?? key } : {}),
          ...(pair?.drill_axis ? { row_id: pair.member_id, axis: pair.drill_axis } : {}),
          indent,
          dimension: dimension.dimension,
          hierarchy: dimension.hierarchy
        },
        (response) => {
          const rows = (response.rows ?? []).map((row) => ({
            ...row,
            ...(pair ? asPairChild(row, pair) : {}),
            drill_key: `${key}${DRILL_KEY_SEPARATOR}${row.member_id}`,
            drill_dimension: response.drill_dimension ?? dimension.dimension,
            drill_hierarchy: response.drill_hierarchy ?? dimension.hierarchy
          }));
          // A re-drill by another dimension replaces this row's breakdown and drops its old sub-drills.
          setChildren((current) => ({
            ...Object.fromEntries(Object.entries(current).filter(([existing]) => !existing.startsWith(`${key}${DRILL_KEY_SEPARATOR}`))),
            [key]: rows
          }));
        }
      );
    },
    [reportId, children, outerChildren, rowAxis, listedRows, hasListedChildren, runDrill]
  );

  const collapse = useCallback(
    (key: string): void => {
      if (hasListedChildren(key)) setCollapsed((current) => new Set([...current, key]));
      setChildren((current) => {
        if (!(key in current)) return current;
        const next: Record<string, readonly ReportQueryRow[]> = {};
        // A collapsed row's descendants go with it, so reopening it starts from one level again.
        const prefix = `${key}${DRILL_KEY_SEPARATOR}`;
        for (const [existing, rows] of Object.entries(current)) {
          if (existing !== key && !existing.startsWith(prefix)) next[existing] = rows;
        }
        return next;
      });
    },
    [hasListedChildren]
  );

  const reset = useCallback((): void => {
    setChildren({});
    setCollapsed(new Set());
    setOuterChildren({});
    setOuterCollapsed(new Set());
  }, []);

  const toggleOuter = useCallback(
    async (member: string, indent: number): Promise<void> => {
      if (!reportId || !rowAxis) return;
      if (hasListedOuterChildren(member)) {
        setOuterCollapsed((current) => (current.has(member) ? new Set([...current].filter((m) => m !== member)) : new Set([...current, member])));
        return;
      }
      if (member in outerChildren) {
        setOuterChildren((current) => withoutOuter(current, member));
        return;
      }
      await runDrill(
        {
          member,
          root: member,
          row_id: member,
          axis: 'outer',
          indent,
          dimension: rowAxis.dimension,
          hierarchy: rowAxis.hierarchy ?? rowAxis.dimension
        },
        (response) => {
          const rows = (response.rows ?? []).map((row) => ({ ...row, drill_key: `${member}${DRILL_KEY_SEPARATOR}${row.member_id}` }));
          setOuterChildren((current) => ({ ...current, [member]: rows }));
        }
      );
    },
    [reportId, rowAxis, outerChildren, hasListedOuterChildren, runDrill]
  );
  const isOuterExpanded = useCallback(
    (member: string): boolean => member in outerChildren || (hasListedOuterChildren(member) && !outerCollapsed.has(member)),
    [outerChildren, outerCollapsed, hasListedOuterChildren]
  );
  const clearError = useCallback((): void => setError(undefined), []);

  const withChildren = useCallback(
    (response: QueryResponse | undefined): QueryResponse | undefined => {
      const untouched = Object.keys(children).length === 0 && collapsed.size === 0;
      if (!response || (untouched && Object.keys(outerChildren).length === 0 && outerCollapsed.size === 0)) return response;
      // A listed row is hidden while any listed ancestor of it -- inner or outer -- is collapsed.
      const collapsedAbove = (start: string | undefined, parents: Map<string, string>, closed: ReadonlySet<string>): boolean => {
        const seen = new Set<string>();
        for (let parent = start && parents.get(start); parent && !seen.has(parent); parent = parents.get(parent)) {
          if (closed.has(parent)) return true;
          seen.add(parent);
        }
        return false;
      };
      const hidden = (row: ReportQueryRow): boolean =>
        collapsedAbove(row.member_id, listedParent, collapsed) || collapsedAbove(row.outer_member, listedOuterParent, outerCollapsed);
      const rows: ReportQueryRow[] = [];
      const place = (row: ReportQueryRow): void => {
        rows.push(row);
        // Spliced by key, so a child set whose parent is no longer in the response is simply not
        // rendered rather than appearing at the end under the wrong row.
        for (const child of children[reportRowKey(row)] ?? []) place(child);
      };
      // An outer line's children follow the LAST row of its run, so its inner breakdown stays together.
      const emit = (sequence: readonly ReportQueryRow[]): void =>
        sequence.forEach((row, index) => {
          place(row);
          const outer = row.outer_member;
          if (outer !== undefined && sequence[index + 1]?.outer_member !== outer) emit(outerChildren[outer] ?? []);
        });
      emit((response.rows ?? []).filter((row) => !hidden(row)));
      return { ...response, rows };
    },
    [children, collapsed, listedParent, outerChildren, outerCollapsed, listedOuterParent]
  );

  const isExpanded = useCallback(
    (key: string): boolean => key in children || (hasListedChildren(key) && !collapsed.has(key)),
    [children, collapsed, hasListedChildren]
  );

  return {
    dimensions,
    loadDimensions,
    drill,
    isExpanded,
    collapse,
    toggleOuter,
    isOuterExpanded,
    reset,
    withChildren,
    busy,
    error,
    clearError,
    serverMs,
    queries
  };
};
